import { baseClient } from "./baseRequest";

// Features and requests change together (counts, statuses), so every query
// lives under one prefix and writes invalidate the whole prefix.
export const FEATURE_REQUESTS_QUERY_PREFIX = ["feature-requests"] as const;
export const PRODUCT_FEATURES_QUERY_KEY = [
  ...FEATURE_REQUESTS_QUERY_PREFIX,
  "features",
] as const;
export const ALL_REQUESTS_QUERY_KEY = [
  ...FEATURE_REQUESTS_QUERY_PREFIX,
  "requests",
] as const;

export enum ProductFeatureStatus {
  Open = "open",
  Planned = "planned",
  InProgress = "in_progress",
  Released = "released",
  Declined = "declined",
}

export enum FeatureRequestStatus {
  Open = "open",
  PotentiallyResolved = "potentially_resolved",
  Fulfilled = "fulfilled",
}

export enum FeatureRequestSource {
  Email = "email",
  Gorgias = "gorgias",
  Review = "review",
  Social = "social",
  Phone = "phone",
  Other = "other",
}

type ChipColor = "default" | "success" | "error" | "info" | "warning";

export const FEATURE_STATUS_META: Record<
  ProductFeatureStatus,
  { label: string; color: ChipColor }
> = {
  [ProductFeatureStatus.Open]: { label: "Open", color: "default" },
  [ProductFeatureStatus.Planned]: { label: "Planned", color: "info" },
  [ProductFeatureStatus.InProgress]: { label: "In progress", color: "warning" },
  [ProductFeatureStatus.Released]: { label: "Released", color: "success" },
  [ProductFeatureStatus.Declined]: { label: "Declined", color: "error" },
};

export const getFeatureStatusMeta = (status?: string | null) =>
  FEATURE_STATUS_META[status as ProductFeatureStatus] ??
  FEATURE_STATUS_META[ProductFeatureStatus.Open];

export const REQUEST_STATUS_META: Record<
  FeatureRequestStatus,
  { label: string; color: ChipColor }
> = {
  [FeatureRequestStatus.Open]: { label: "Open", color: "default" },
  [FeatureRequestStatus.PotentiallyResolved]: {
    label: "Potentially resolved",
    color: "warning",
  },
  [FeatureRequestStatus.Fulfilled]: { label: "Fulfilled", color: "success" },
};

export const getRequestStatusMeta = (status?: string | null) =>
  REQUEST_STATUS_META[status as FeatureRequestStatus] ??
  REQUEST_STATUS_META[FeatureRequestStatus.Open];

export const FEATURE_REQUEST_SOURCE_LABELS: Record<
  FeatureRequestSource,
  string
> = {
  [FeatureRequestSource.Email]: "Email",
  [FeatureRequestSource.Gorgias]: "Gorgias",
  [FeatureRequestSource.Review]: "Review",
  [FeatureRequestSource.Social]: "Social",
  [FeatureRequestSource.Phone]: "Phone",
  [FeatureRequestSource.Other]: "Other",
};

export type ProductFeature = {
  id: string;
  title: string;
  description: string | null;
  status: ProductFeatureStatus;
  deployedVersion: string | null;
  deployedAt: number | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  requestCount: number;
  // distinct customer emails
  customerCount: number;
  openCount: number;
  potentiallyResolvedCount: number;
  fulfilledCount: number;
  lastRequestedAt: number | null;
};

export type FeatureRequest = {
  id: string;
  featureId: string;
  featureTitle: string;
  featureDeployedVersion: string | null;
  customerEmail: string;
  customerName: string | null;
  requestedAt: number;
  source: FeatureRequestSource;
  notes: string | null;
  status: FeatureRequestStatus;
  fulfilledAt: number | null;
  notifiedAt: number | null;
  createdBy: string;
  createdAt: string;
};

export type CreateProductFeatureInput = {
  title: string;
  description?: string | null;
};

export type UpdateProductFeatureInput = Partial<CreateProductFeatureInput> & {
  status?: ProductFeatureStatus;
};

export type CreateFeatureRequestInput = {
  featureId: string;
  customerEmail: string;
  customerName?: string | null;
  requestedAt?: number;
  source?: FeatureRequestSource;
  notes?: string | null;
};

export type UpdateFeatureRequestInput = Partial<CreateFeatureRequestInput>;

export type NotifyRequestsInput = {
  requestIds: string[];
  message?: string | null;
  dryRun?: boolean;
};

export type NotifyRequestsGroup = {
  featureId: string;
  featureTitle: string;
  recipients: string[];
  requestIds: string[];
};

export type NotifyRequestsResult = {
  dryRun: boolean;
  groups: NotifyRequestsGroup[];
  alreadyNotifiedCount: number;
};

const featureRequestsClient = {
  listFeatures: async (): Promise<ProductFeature[]> =>
    baseClient.get<ProductFeature[]>({ url: "/admin/product-features" }),

  createFeature: async (
    input: CreateProductFeatureInput,
  ): Promise<ProductFeature> =>
    baseClient.post<ProductFeature>({
      url: "/admin/product-features",
      body: input,
    }),

  updateFeature: async (
    id: string,
    input: UpdateProductFeatureInput,
  ): Promise<ProductFeature> =>
    baseClient.put<ProductFeature>({
      url: `/admin/product-features/${id}`,
      body: input,
    }),

  deleteFeature: async (id: string): Promise<void> =>
    baseClient.delete<void>({ url: `/admin/product-features/${id}` }),

  mergeFeature: async (id: string, intoId: string): Promise<ProductFeature> =>
    baseClient.post<ProductFeature>({
      url: `/admin/product-features/${id}/merge`,
      body: { intoId },
    }),

  deployFeature: async (
    id: string,
    version: string,
  ): Promise<{ feature: ProductFeature; flaggedCount: number }> =>
    baseClient.post({
      url: `/admin/product-features/${id}/deploy`,
      body: { version },
    }),

  undeployFeature: async (id: string): Promise<ProductFeature> =>
    baseClient.delete<ProductFeature>({
      url: `/admin/product-features/${id}/deploy`,
    }),

  listRequests: async (): Promise<FeatureRequest[]> =>
    baseClient.get<FeatureRequest[]>({ url: "/admin/feature-requests" }),

  createRequest: async (
    input: CreateFeatureRequestInput,
  ): Promise<FeatureRequest> =>
    baseClient.post<FeatureRequest>({
      url: "/admin/feature-requests",
      body: input,
    }),

  updateRequest: async (
    id: string,
    input: UpdateFeatureRequestInput,
  ): Promise<FeatureRequest> =>
    baseClient.put<FeatureRequest>({
      url: `/admin/feature-requests/${id}`,
      body: input,
    }),

  setRequestsStatus: async (
    requestIds: string[],
    status: FeatureRequestStatus,
  ): Promise<{ modifiedCount: number }> =>
    baseClient.post({
      url: "/admin/feature-requests/.status",
      body: { requestIds, status },
    }),

  deleteRequests: async (
    requestIds: string[],
  ): Promise<{ deletedCount: number }> =>
    baseClient.post({
      url: "/admin/feature-requests/.delete",
      body: { requestIds },
    }),

  notifyRequests: async (
    input: NotifyRequestsInput,
  ): Promise<NotifyRequestsResult> =>
    baseClient.post<NotifyRequestsResult>({
      url: "/admin/feature-requests/.notify",
      body: input,
    }),
};

export default featureRequestsClient;
